import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowRight } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_95ed2209-20a6-4af0-9c41-ce1277420ecb/artifacts/fy04zhmm_10DJ-80A%20VALVE%20IMAGE%20KITZ.jpg";

const LINES = ["MORE THAN VALVES.", "YOUR PARTNER IN", "FLOW CONTROL."];

const lineVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const wordVariants = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-[#0A1626]">
      {/* Dark overlay is scoped to this hero image ONLY, matching Summit Technodyne's own hero treatment — the rest of the site is light. */}
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img src={HERO_IMG} alt="KITZ valve actuator installation" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1626] via-[#0A1626]/70 to-[#0A1626]/30" />
        <div className="absolute inset-0 bg-[#0A1626]/20" />
      </motion.div>

      <div className="grain-overlay" />

      <div className="relative z-10 max-w-[1600px] w-full mx-auto px-6 sm:px-10 pb-20 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-overline"
          className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF6F3C] mb-6"
        >
          SUMATECH INNOVATIONS LLP &middot; Est. 2023
        </motion.p>

        <motion.h1
          initial="hidden"
          animate="visible"
          variants={lineVariants}
          data-testid="hero-headline"
          className="font-heading font-black uppercase text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={wordVariants} className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-8 max-w-xl font-body text-white/75 text-base sm:text-lg leading-relaxed"
        >
          Your KITZ Valve Partner &mdash; delivering the right product, technical expertise, and reliable
          support for every process. We distribute and engineer KITZ valve systems across commercial,
          industrial, and semiconductor verticals &mdash; matching the right product line to the right
          pressure, purity, and process.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid={LANDING.heroCtaProducts}
            onClick={() => scrollTo("#products")}
            className="group inline-flex items-center gap-3 bg-[#FF6F3C] text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#e85f2f] transition-colors duration-200"
          >
            Explore Product Lines
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            data-testid={LANDING.heroCtaContact}
            onClick={() => scrollTo("#contact")}
            className="inline-flex items-center gap-3 border border-white/40 text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-[#FF6F3C] hover:text-[#FF6F3C] transition-colors duration-200"
          >
            Talk to an Engineer
          </button>
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        className="relative z-10 mx-auto mb-8 text-white/60"
      >
        <ArrowDown size={20} />
      </motion.div>
    </section>
  );
};

export default Hero;
